'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Zap, Download, RefreshCw, Eye, Calendar, Receipt } from 'lucide-react';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type { DateRange } from 'react-day-picker';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { BillingPreviewCard } from './BillingPreviewCard';
import { useBillingPreview, useTriggerManualBilling } from '@/lib/api/account-manager';

interface ManualBillingTabProps {
  accountId: string | undefined;
  accountPlanLoading: boolean;
  billingDisabled?: boolean;
}

// Dates from DateRangePicker (UTC mode) are UTC midnight — use UTC getters
const startOfDayUTC = (date: Date): Date => {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
};

const endOfDayUTC = (date: Date): Date => {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999));
};

export function ManualBillingTab({ accountId, accountPlanLoading, billingDisabled = false }: ManualBillingTabProps) {
  const { t } = useTranslation('common');
  // Date range state - default to current billing week (Monday-Sunday) in UTC
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const now = new Date();
    const utcDay = now.getUTCDay();
    const daysSinceMonday = (utcDay + 6) % 7;
    const monday = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() - daysSinceMonday
    ));
    const sunday = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() - daysSinceMonday + 6
    ));
    return { from: monday, to: sunday };
  });

  const [previewPeriod, setPreviewPeriod] = useState<{ start?: string; end?: string }>({});
  const [isDownloading, setIsDownloading] = useState(false);

  // Hooks
  const { data: previewData, refetch: refetchPreview, isFetching: isFetchingPreview } = useBillingPreview(
    previewPeriod.start,
    previewPeriod.end
  );
  const triggerBillingMutation = useTriggerManualBilling();

  const handlePreview = useCallback(async () => {
    if (!dateRange.from || !dateRange.to) {
      toast.error('Please select a date range');
      return;
    }

    const fromDate = startOfDayUTC(dateRange.from);
    const toDate = endOfDayUTC(dateRange.to);

    setPreviewPeriod({
      start: fromDate.toISOString(),
      end: toDate.toISOString(),
    });

    await refetchPreview();
  }, [dateRange, refetchPreview]);

  const handleDownloadCSV = useCallback(async () => {
    if (!accountId) {
      toast.error('Account ID not found');
      return;
    }

    if (!dateRange.from || !dateRange.to) {
      toast.error('Please select a date range');
      return;
    }

    setIsDownloading(true);
    try {
      const fromDate = startOfDayUTC(dateRange.from);
      const toDate = endOfDayUTC(dateRange.to);

      const response = await fetch('/api/billing/preview-csv', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accountId,
          periodStart: fromDate.toISOString(),
          periodEnd: toDate.toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate CSV');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing-${format(dateRange.from, 'yyyy-MM-dd')}-to-${format(dateRange.to, 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('CSV downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download CSV');
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  }, [accountId, dateRange]);

  const handleTriggerBilling = useCallback(async () => {
    if (!accountId) {
      toast.error('Account ID not found');
      return;
    }

    if (!dateRange.from || !dateRange.to) {
      toast.error('Please select a date range');
      return;
    }

    const fromDate = startOfDayUTC(dateRange.from);
    const toDate = endOfDayUTC(dateRange.to);

    triggerBillingMutation.mutate({
      accountId,
      periodStart: fromDate.toISOString(),
      periodEnd: toDate.toISOString(),
    });
  }, [accountId, dateRange, triggerBillingMutation]);

  const isFormDisabled = accountPlanLoading || !accountId || billingDisabled;
  const hasValidRange = dateRange.from && dateRange.to;

  return (
    <div className="space-y-6">
      {billingDisabled && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="py-4 text-sm">
            {t('accountManager.manualBilling.billingDisabledBanner')}
          </CardContent>
        </Card>
      )}
      {/* Date Selection Card */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Billing Period
          </CardTitle>
          <CardDescription>
            Select a date range to preview or generate billing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            timezone="UTC"
          />
          {hasValidRange && (
            <p className="text-sm text-muted-foreground">
              {formatInTimeZone(dateRange.from!, 'UTC', 'MMM d, yyyy')} 00:00 – {formatInTimeZone(dateRange.to!, 'UTC', 'MMM d, yyyy')} 23:59 UTC
            </p>
          )}
        </CardContent>
      </Card>

      {/* Actions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Actions
          </CardTitle>
          <CardDescription>
            Preview billing data or generate invoices for the selected period
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Preview Report */}
            <div className="p-4 border rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-md">
                  <Eye className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm">Preview Report</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    View billing estimate without creating records
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreview}
                    disabled={!hasValidRange || isFetchingPreview || isFormDisabled}
                    className="mt-3 w-full"
                  >
                    {isFetchingPreview ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Eye className="h-4 w-4 mr-2" />
                    )}
                    Preview
                  </Button>
                </div>
              </div>
            </div>

            {/* Download CSV */}
            <div className="p-4 border rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-md">
                  <Download className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm">Export CSV</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Download transactions as CSV file
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadCSV}
                    disabled={!hasValidRange || isDownloading || isFormDisabled}
                    className="mt-3 w-full"
                  >
                    {isDownloading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    {isDownloading ? 'Generating...' : 'Download'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Trigger Billing */}
            <div className="p-4 border rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-md">
                  <Zap className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm">Trigger Billing</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Create invoice and send notifications
                  </p>
                  <Button
                    size="sm"
                    onClick={handleTriggerBilling}
                    disabled={!hasValidRange || triggerBillingMutation.isPending || isFormDisabled}
                    className="mt-3 w-full"
                  >
                    {triggerBillingMutation.isPending ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4 mr-2" />
                    )}
                    {triggerBillingMutation.isPending ? 'Processing...' : 'Generate Invoice'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing Preview Results */}
      {(previewData?.preview || previewPeriod.start) && (
        <BillingPreviewCard
          preview={previewData?.preview}
          isLoading={false}
          isFetching={isFetchingPreview}
          periodStart={previewPeriod.start ? new Date(previewPeriod.start) : undefined}
          periodEnd={previewPeriod.end ? new Date(previewPeriod.end) : undefined}
        />
      )}
    </div>
  );
}
